import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerAccount, StockCompanyWithWeight } from '@inyova/models';
import { PageScrollService } from 'ngx-page-scroll-core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { Bond } from '@app/app.constants';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { Languages, LanguageService } from '@shared/services/language.service';

@Component({
  selector: 'app-setup-portfolio-stocks-bonds',
  templateUrl: './setup-portfolio-stocks-bonds.component.html',
  styleUrls: ['./setup-portfolio-stocks-bonds.component.scss'],
  standalone: false
})
export class SetupPortfolioStocksBondsComponent implements OnDestroy, OnInit {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() appLocation: 'ch' | 'de';
  @Input() investmentData: CustomerInvestmentData;
  @Input() step: number;
  @Input() kind: '3b' | '3a' | 'kid';
  @Input() bonds: Bond[];

  portfolioCompaniesList: StockCompanyWithWeight[];
  editMode = false;
  yourStocks = true;
  stocksCount: number;
  selectedBondID = 0;
  selectedCompanyID = null;
  showAll = false;
  language: Languages;
  currentAccount: CustomerAccount;
  someZeroPercentage = false;
  MINIMUM_COMPANIES = 8;
  shownCompanies = [];
  riskName = 'balanced';

  constructor(
    private el: ElementRef,
    private store: Store,
    public router: Router,
    public http: HttpClient,
    private languageService: LanguageService,
    private scrollService: PageScrollService,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngOnInit() {
    this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;
    });

    this.language = this.languageService.selected.value;

    this.store.pipe(select(fromStrategy.selectStockCompanies), takeUntil(this.onDestroy$)).subscribe((stocks) => {
      this.portfolioCompaniesList = this.sortByOrder([...stocks]);
      this.stocksCount = this.portfolioCompaniesList.length;
      this.someZeroPercentage = [...this.portfolioCompaniesList].some((company) => company.position_weight === 0);
      this.setShownCompanies();
    });

    this.store.pipe(select(fromAppRoot.selectCurrentAccountEditMode), takeUntil(this.onDestroy$)).subscribe((editMode) => {
      this.editMode = editMode;
    });

    this.store.pipe(select(fromStrategy.selectRiskName), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.riskName = value.riskName;
    });
  }

  sortByOrder(list: StockCompanyWithWeight[]): StockCompanyWithWeight[] {
    if (list.length === 0) {
      return [];
    }
    return list.sort((a, b) => a.inyova_order - b.inyova_order);
  }

  changeTab(type: string): void {
    this.yourStocks = type === 'stock';
  }

  updateCompanyDetail(event: { companyID: number; updated: boolean }): void {
    this.selectedCompanyID = event.companyID;
    setTimeout(() => {
      this.scrollService.scroll({
        document: this.document,
        scrollTarget: this.el.nativeElement.querySelector(`[id='${this.selectedCompanyID}']`),
        duration: 200,
        scrollOffset: 120
      });
    }, 100);
  }

  changeSelectedBondID(bondID: number): void {
    if (this.selectedBondID === bondID) {
      this.selectedBondID = 0;
      return;
    }
    this.selectedBondID = bondID;
  }

  tabNavigation(tabIndex: number): void {
    this.store.dispatch(StrategyActions.setTabIndex({ tabIndex }));
  }

  showAllCompanies(): void {
    this.showAll = !this.showAll;
    this.setShownCompanies();
  }

  setShownCompanies(): void {
    this.shownCompanies = this.showAll ? this.portfolioCompaniesList : this.portfolioCompaniesList.slice(0, this.MINIMUM_COMPANIES);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
